/**
 * DeviceContext — Item 5 (Master Control Center · Multi-Device Orchestration)
 *
 * Every open browser tab is a "device". Only one device per venue may be
 * the audio *master* at a time — the rest are read-only "remote" mini
 * players. This context owns:
 *
 *   - deviceId       stable UUID per tab (localStorage-persisted)
 *   - isMaster       true if THIS tab is currently the audio master
 *   - masterDeviceId which device (by id) currently holds the master flag
 *   - devices        the live-device list (for booth "N devices connected")
 *   - takeover()     user clicked "Take Over" — force this tab to master
 *
 * The MusicContext consumes `isMasterRef` (via useDeviceRef) and refuses
 * to start audio when not master — this is what actually prevents the
 * "double-tab, double-audio" Apple MusicKit bug. See MusicContext.js
 * around play()/playTracks() for the guard.
 *
 * Only mounted for authed team members. Anonymous/guest routes skip this
 * context entirely — no device registration for guests.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_MS = 15000;
const DEVICE_ID_KEY = 'mb.device_id';

// UUID-v4 (RFC 4122) — no dep needed
const genDeviceId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const shortLabel = () => {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  // "Mozilla/5.0 (Macintosh; ...) Chrome/..." → "Macintosh · Chrome"
  const os = /Windows/.test(ua) ? 'Windows'
    : /Macintosh|Mac OS X/.test(ua) ? 'Mac'
    : /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android'
    : /Linux/.test(ua) ? 'Linux'
    : 'Web';
  const br = /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Browser';
  return `${os} · ${br}`;
};

const DeviceContext = createContext(null);

export const DeviceProvider = ({ children }) => {
  const { user } = useAuth();
  const [deviceId] = useState(() => {
    try {
      const existing = localStorage.getItem(DEVICE_ID_KEY);
      if (existing) return existing;
      const fresh = genDeviceId();
      localStorage.setItem(DEVICE_ID_KEY, fresh);
      return fresh;
    } catch { return genDeviceId(); }
  });
  const [isMaster, setIsMaster] = useState(true);   // optimistic: assume master until backend says otherwise
  const [masterDeviceId, setMasterDeviceId] = useState(null);
  const [devices, setDevices] = useState([]);
  const [enabled, setEnabled] = useState(false);
  // Item 5B: RemoteBus waits for `ready` before opening a WebSocket so it
  // doesn't open-then-immediately-close on the initial optimistic-master
  // render (which surfaced as a benign but noisy "closed before connection
  // established" warning in dev consoles).
  const [ready, setReady] = useState(false);

  // ref lets the MusicContext read isMaster synchronously inside its
  // play()/playTracks() callbacks WITHOUT taking a dep on this state
  // (which would cause every playback-related useCallback to churn).
  const isMasterRef = useRef(true);
  useEffect(() => { isMasterRef.current = isMaster; }, [isMaster]);

  // Only participate in the registry when the user is authed AND acting as
  // a venue role (host, team_lead, superadmin). Guest routes / login page
  // must not register — they don't play audio.
  useEffect(() => {
    if (!user || !user.id) { setEnabled(false); return; }
    const role = user.role || 'client';
    setEnabled(role === 'client' || role === 'team_lead' || role === 'superadmin');
  }, [user]);

  const applyResponse = useCallback((r) => {
    if (!r || typeof r !== 'object') return;
    if (typeof r.is_master === 'boolean') setIsMaster(r.is_master);
    if (typeof r.master_device_id === 'string' || r.master_device_id === null) setMasterDeviceId(r.master_device_id);
    if (Array.isArray(r.devices)) setDevices(r.devices);
    // Any successful response means the backend has an authoritative view
    // of this device's role — safe to open the WS now.
    setReady(true);
  }, []);

  // Register + start the heartbeat loop. Sends label so the booth shows a
  // human name ("Mac · Chrome") not a raw UUID.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let hbTimer = null;

    const doRegister = async () => {
      try {
        const r = await axios.post(`${API}/playback/devices/register`, {
          device_id: deviceId,
          label: shortLabel(),
          want_master: true,
        });
        if (!cancelled) applyResponse(r.data);
      } catch (err) {
        console.warn('[Device] register failed:', err?.message);
      }
    };

    const doHeartbeat = async () => {
      try {
        const r = await axios.post(`${API}/playback/devices/heartbeat`, {
          device_id: deviceId,
        });
        if (!cancelled) applyResponse(r.data);
      } catch (err) {
        // silent; will retry next tick
      }
    };

    doRegister();
    hbTimer = setInterval(doHeartbeat, HEARTBEAT_MS);
    // Also heartbeat when the tab is refocused — catches the "device
    // marked stale while backgrounded" edge case quickly.
    const onFocus = () => { doHeartbeat(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      cancelled = true;
      if (hbTimer) clearInterval(hbTimer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [enabled, deviceId, applyResponse]);

  const takeover = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/playback/devices/takeover`, {
        device_id: deviceId,
      });
      applyResponse(r.data);
      toast.success('This tab is now the audio Master');
    } catch (err) {
      toast.error('Could not take over — try again');
    }
  }, [deviceId, applyResponse]);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/playback/devices/list`);
      applyResponse(r.data);
    } catch { /* soft-fail */ }
  }, [applyResponse]);

  const value = {
    deviceId,
    isMaster,
    masterDeviceId,
    devices,
    isMasterRef,
    enabled,
    ready,
    takeover,
    refresh,
  };

  return <DeviceContext.Provider value={value}>{children}</DeviceContext.Provider>;
};

export const useDevice = () => {
  const ctx = useContext(DeviceContext);
  if (!ctx) {
    // Called outside the provider (e.g., on the public /venue/:code route).
    // Return a safe stub that acts as "solo master" — MusicContext's guard
    // becomes a no-op, and the app behaves as it did pre-Item-5.
    return {
      deviceId: null,
      isMaster: true,
      masterDeviceId: null,
      devices: [],
      isMasterRef: { current: true },
      enabled: false,
      ready: false,
      takeover: async () => {},
      refresh: async () => {},
    };
  }
  return ctx;
};
